import React, { useEffect, useMemo } from 'react';
import { trackAction } from 'utils/trackAction';
import { makeStyles, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import { useGetUserByIdQuery, useGetDailyStatusHistoryQuery, useGetForecastsForUserQuery, usePrefetch, useGetWeeklyHoursQuery, useGetExperienceProfileQuery } from 'utils/wemble-api';
import organization from 'utils/organization';
import usePermissions from 'hooks/usePermissions';
import WeeklyHoursWidget from './WeeklyHoursWidget';
import RecentMattersTable from './RecentMattersTable';
import UserCompetencesTable from './UserCompetencesTable';
import ExperienceProfile from './ExperienceProfile';
import { getStatusFromUser } from 'components/Home/Forecast/utils';
import { useCardTableStyles } from './styles';
import StatusHistory from './StatusHistory';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    height: '100%',
    minHeight: 600,
    margin: -16,
    [theme.breakpoints.down('sm')]: {
      margin: -15,
      overflowX: 'hidden',
    },
    [theme.breakpoints.down(1100)]: {
      flexDirection: 'column',
      height: 'auto',
      minHeight: 'unset',
    },
  },
  sidebar: {
    width: 310,
    borderRight: `1px solid ${theme.palette.divider}`,
    padding: '18px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    [theme.breakpoints.down(1100)]: {
      width: '100%',
      borderRight: 'none',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  mainContent: {
    flex: 1,
    padding: theme.spacing(2.5),
    overflowY: 'auto',
    [theme.breakpoints.down(1100)]: {
      padding: theme.spacing(1.5),
      overflowY: 'visible',
    },
  },
  userTitle: {
    fontSize: 22,
    fontWeight: 600,
    lineHeight: 1.2,
    color: theme.palette.text.primary,
  },
  userSubtitle: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    marginTop: 4,
  },
  propertySection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  propertyLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    color: theme.palette.gray.g2,
    marginBottom: 1,
  },
  propertyCard: {
    backgroundColor: theme.palette.gray.g7,
    borderRadius: 8,
    padding: '8px 12px',
  },
  propertyValue: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  propertyValueSmall: {
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-all' as const,
  },
}));

const formatDate = (date: string) => {
  return date.substr(0, 10).replaceAll('-', '.');
};

/** A group name that opens the home page filtered to that group (SearchFilter reads ?group=). */
const GroupLink = ({ id, name, className }: { id?: string | null; name?: string | null; className: string }) => {
  const history = useHistory();
  const { pathname } = useLocation();
  if (!id) return <div className={className}>{name}</div>;
  // Already on /home: replace, so Back doesn't land on the same page again.
  const go = pathname === '/home' ? history.replace : history.push;
  return (
    <div
      className={className}
      title={`Show everyone in ${name}`}
      onClick={() => go(`/home?group=${id}`)}
      style={{ cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'rgba(0,0,0,0.25)', textUnderlineOffset: 3 }}
    >
      {name}
    </div>
  );
};

const UserCard = ({ userId }: { userId: string }) => {
  const history = useHistory();
  const classes = useStyles();
  const tableClasses = useCardTableStyles();
  const { data: user } = useGetUserByIdQuery({ id: userId }, { refetchOnMountOrArgChange: 5 });
  const prefetchProject = usePrefetch('getProject');
  const { currentUser, isAdmin, canViewExperience: showExperience } = usePermissions();
  const isOwnProfile = currentUser?._id === userId;

  // Track profile view when modal mounts (not on hover prefetch).
  // isOwnProfile is derivable from heartbeat.user === details.profileUserId, so not stored.
  // Wait for the name to resolve so we only log once with the full details.
  const viewedName = user?.name;
  useEffect(() => {
    if (viewedName) trackAction('viewProfile', { profileUserId: userId, profileName: viewedName });
  }, [userId, viewedName]);
  const canViewStatusHistory = isOwnProfile || isAdmin;
  const { data: dailyStatusHistory } = useGetDailyStatusHistoryQuery({
    userId,
    startDate: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
    endDate: moment().endOf('day').format('YYYY-MM-DD'),
  }, { skip: !canViewStatusHistory });
  const { data: forecasts } = useGetForecastsForUserQuery({ userId }, { skip: !canViewStatusHistory });

  const currentStatus = getStatusFromUser(user);
  const statusHistoryData = useMemo(() => {
    const data = { ...dailyStatusHistory };
    if (currentStatus !== undefined) {
      data[moment().format('YYYY-MM-DD')] = {
        workload: currentStatus === 'away' ? null : Number(currentStatus),
        isAway: currentStatus === 'away',
      };
    }
    return data;
  }, [dailyStatusHistory, currentStatus]);

  const org = organization(currentUser?.company);

  // Permissions: two systems control access to matters/hours data.
  //
  // 1. Per-user privileges (HS): When org.detailedViewAdminDefault is false, the UserModal
  //    shows per-user checkboxes under "Matter data access". Admins set these individually:
  //    - detailedViewPriviliges: can see the matters/timekeepers view
  //    - detailedViewHoursPriviliges: can see hours (requires detailedViewPriviliges)
  //    - utilizationViewOthersPriviliges: can see other people's utilization data
  //
  // 2. Admin-default (Synch, Ström, Born): When org.detailedViewAdminDefault is true,
  //    no per-user checkboxes are shown. Any admin/companyAdmin/administratorPriviliges
  //    user gets access automatically. Non-admin users have no access.
  //
  // Own profile: users can always see their own hours regardless of privileges.
  // Backend mirrors this: endpoints check isOwnProfile || privilege || admin.
  const showMatters = org.enableUtilizationView && (
    currentUser?.detailedViewPriviliges
    || (org.detailedViewAdminDefault && (currentUser?.admin || currentUser?.administratorPriviliges || currentUser?.companyAdmin))
    || org.experienceView === 'all'
  )

  const enableHours = org.enableDetailedViewHours && (isOwnProfile || currentUser?.detailedViewHoursPriviliges || ((currentUser?.admin || currentUser?.administratorPriviliges || currentUser?.companyAdmin) && org.detailedViewAdminDefault))

  const { data: weeklyHours } = useGetWeeklyHoursQuery({ userId, weeks: 4 }, { skip: !enableHours });

  const { data: experienceData } = useGetExperienceProfileQuery({ userId }, { skip: !showExperience });

  if (!user) return null;

  const weeks = weeklyHours || [];

  return (
    <div className={classes.container}>
      {/* Sidebar */}
      <div className={classes.sidebar}>
        <div>
          <div className={classes.userTitle}>{user.name}</div>
          {user.title && <div className={classes.userSubtitle}>{user.title}</div>}
        </div>

        {enableHours && (
          <div>
            <div className={classes.propertyLabel}>Recent Weeks</div>
            {weeks.length > 0 ? (
              <WeeklyHoursWidget weeks={weeks} />
            ) : weeklyHours !== undefined && weeks.length === 0 ? (
              <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                No time entry data available for {user.name}
              </div>
            ) : null}
          </div>
        )}

        {user?.admin ? (
          <div>
            <div className={classes.propertyLabel}>Account Type</div>
            <div className={classes.propertyCard}>
              <div className={classes.propertyValue}>Observer</div>
            </div>
          </div>
        ) : canViewStatusHistory ? (
          <div>
            <div className={classes.propertyLabel}>Status History</div>
            <StatusHistory data={statusHistoryData} forecasts={forecasts || []} />
          </div>
        ) : null}

        <div className={classes.propertySection}>
          {user.group && typeof user.group !== 'string' && (
            <div>
              <div className={classes.propertyLabel}>Group</div>
              <div className={classes.propertyCard}>
                <GroupLink id={user.group._id} name={user.group.name} className={classes.propertyValue} />
              </div>
            </div>
          )}

          {user.secondaryGroup && typeof user.secondaryGroup !== 'string' && (
            <div>
              <div className={classes.propertyLabel}>Secondary Group</div>
              <div className={classes.propertyCard}>
                <GroupLink id={user.secondaryGroup._id} name={user.secondaryGroup.name} className={classes.propertyValue} />
              </div>
            </div>
          )}

          {user.office && typeof user.office !== 'string' && (
            <div>
              <div className={classes.propertyLabel}>Office</div>
              <div className={classes.propertyCard}>
                <div className={classes.propertyValue}>{user.office.name}</div>
              </div>
            </div>
          )}

          {user.email && (
            <div>
              <div className={classes.propertyLabel}>Email</div>
              <div className={classes.propertyCard}>
                <a
                  href={`mailto:${user.email}`}
                  className={classes.propertyValueSmall}
                  style={{ display: 'block', textDecoration: 'underline', textDecorationColor: 'rgba(0,0,0,0.25)', textUnderlineOffset: 3 }}
                >
                  {user.email}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className={classes.mainContent}>
        {showMatters && (
          <RecentMattersTable userId={userId} defaultDays={14} enableHours={!!enableHours} enableComments={!!org.enableDetailedViewComments} />
        )}

        {experienceData?.entries?.length > 0 && (
          <ExperienceProfile
            userId={userId}
            entries={experienceData.entries}
            dataAvailableSince={experienceData.dataAvailableSince}
            userName={user.name?.split(' ')[0]}
            canEditStars={isOwnProfile}
            showHours={!!enableHours}
          />
        )}


        {(!org.disableProjects && user.projects && user.projects.length) ? (<div className={tableClasses.section}>
          <h2>Projects</h2>
          <Table className={tableClasses.table}>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell align={'right'}>Starts</TableCell>
                <TableCell align={'right'}>Ends</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {user.projects?.map(
                (project) =>
                  typeof project !== 'string' && (
                    <React.Fragment key={`user-card-project-${project._id}`}>
                      <TableRow
                        onClick={() => {
                          history.push({ pathname: `/projects/${project._id}`, state: project });
                        }}
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={() => project._id && prefetchProject({ id: project._id }, { ifOlderThan: 180 })}
                      >
                        <TableCell>{project.name}</TableCell>
                        <TableCell align={'right'} style={{ paddingBlock: 0 }}>
                          {project.startDate && (
                            <div className={tableClasses.datePill}>{formatDate(project.startDate)}</div>
                          )}
                        </TableCell>
                        <TableCell align={'right'} style={{ paddingBlock: 0 }}>
                          {project.endDate && <div className={tableClasses.datePill}>{formatDate(project.endDate)}</div>}
                        </TableCell>
                      </TableRow>
                      <TableRow className={tableClasses.spacer} />
                    </React.Fragment>
                  ),
              )}
            </TableBody>
          </Table>
        </div>
        ) : ''}
        {!org.disableCompetences && (
          <UserCompetencesTable user={user} />
        )}
      </div>
    </div>
  );
};
export default UserCard;
